import { useState } from 'react';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import {
    Chip,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
} from '@mui/material';
import {
    Identifier,
    RecordContextProvider,
    SimpleListLoading,
    useListContext,
} from 'react-admin';
import { Link } from 'react-router-dom';

import { defaultLeadStatuses } from '@/root/defaultConfiguration';
import { formatRelativeBackendDate } from '@/utils/date_utils';
import { Lead } from '@/types';
import { LeadListNextStepsDialog } from './LeadListNextStepsDialog';

const statusLabel = (status: string) =>
    defaultLeadStatuses.find(item => item.value === status)?.label || status;

export const LeadListContent = () => {
    const { data: leads, error, isPending } = useListContext<Lead>();
    const [stepsLeadId, setStepsLeadId] = useState<Identifier | null>(null);
    const now = Date.now();

    if (isPending) {
        return <SimpleListLoading hasSecondaryText />;
    }
    if (error) {
        return null;
    }

    return (
        <>
            <List dense>
                {leads.map(item => (
                    <RecordContextProvider key={item.id} value={item}>
                        <ListItem
                            disablePadding
                            secondaryAction={
                                <IconButton
                                    edge="end"
                                    aria-label="Next steps"
                                    onClick={event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        setStepsLeadId(item.id);
                                    }}
                                >
                                    <FormatListNumberedIcon />
                                </IconButton>
                            }
                        >
                            <ListItemButton
                                component={Link}
                                to={`/leads/${item.id}/show`}
                            >
                                <ListItemText
                                    primary={item.name?.trim() || 'Unnamed lead'}
                                    secondary={[
                                        item.email,
                                        item.phone_number,
                                        item.created_by_name || 'Unknown',
                                        item.created_at
                                            ? formatRelativeBackendDate(
                                                  item.created_at,
                                                  now
                                              )
                                            : null,
                                    ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                    secondaryTypographyProps={{
                                        variant: 'caption',
                                    }}
                                />
                                <Chip
                                    size="small"
                                    label={statusLabel(item.status)}
                                    sx={{ ml: 1, mr: 1 }}
                                />
                            </ListItemButton>
                        </ListItem>
                    </RecordContextProvider>
                ))}
                {leads.length === 0 && (
                    <ListItem>
                        <ListItemText primary="No leads found" />
                    </ListItem>
                )}
            </List>
            {stepsLeadId != null ? (
                <LeadListNextStepsDialog
                    leadId={stepsLeadId}
                    onClose={() => setStepsLeadId(null)}
                />
            ) : null}
        </>
    );
};
